import { extend } from 'dayjs';
import { IDate } from '../date';
import { IArrival } from './arrival';
import { IBusiness } from './businessBase';
import { IAnnounce } from './forecast';
/**
 * 未管理の取引
 * 未管理の取引は、未転記の取引を指す
 * 未管理ページで不足情報を入力し、マネージド取引(IBusiness)に転記する
 */
export interface IUnmanagedBusiness
  extends Partial<IUnmanagedBusinessTypeAnnounce>,
    Partial<IUnmanagedBusinessTypeSalesResult>,
    Partial<IUnmanagedBusinessTypeInventory>,
    Partial<IUnmanagedBusinessTypeArrival> {
  /**
   * 取引ID
   */
  businessId: IBusiness['businessId'];
  /**
   * 集計行ID
   */
  aggregateBusinessId: IBusiness['businessId'];
  /**
   * 商品CD
   */
  businessNumber: IBusiness['businessNumber'];
  /**
   * 商品番号
   */
  businessName: IBusiness['businessName'];
  /**
   * 仕入先名
   */
  supplierName: IBusiness['supplierName'];
  /**
   * 仕入先コード
   */
  supplierNumber: IBusiness['supplierNumber'];
  /**
   * 置き場名
   */
  warehouse: IBusiness['warehouse'];
  /**
   * 置き場コード
   */
  warehouseNumber: IBusiness['warehouseNumber'];
  /**
   * 容量
   */
  package: IBusiness['package'];
  /**
   * エクセルファイルの種類
   */
  importFileType: ImportFileType;
  /**
   * 入力日
   */
  inputDate: IDate;
  /**
   * エクセルのファイル名
   */
  fileName: string;
  /**
   * MOQ
   */
  moq: IBusiness['MOQ'];
  /**
   * 単位
   */
  unit: IBusiness['unit'];
  /**
   * リードタイム
   */
  leadTime: IBusiness['leadTime'];
  /**
   * リードタイムの単位
   */
  leadTimeUnit: IBusiness['leadTimeUnit'];
  /**
   * 標準在庫指数
   */
  stdInventoryIndices: IBusiness['stdInventoryIndices'];
  /**
   * 在庫日
   */
  executionDate: IDate;
  /**
   * ベースとなる取引ID
   */
  baseBusinessId: IBusiness['businessId'] | undefined;
}

/**
 * 未管理の取引の内示に関する情報（共通事項を除く）
 */
export interface IUnmanagedBusinessTypeAnnounce {
  /**
   * 内示
   */
  announce: IAnnounce[];
  /**
   * 売り上げ先名
   */
  customerName: IBusiness['customerName'];
  /**
   * 売り上げ先コード
   */
  customerNumber: IBusiness['customerNumber'];
  /**
   * 送り先名
   */
  salesDestinationName: IBusiness['salesDestinationName'];
  /**
   * 送り先コード
   */
  salesDestinationNumber: IBusiness['salesDestinationNumber'];
}

/**
 * 未管理の取引の販売実績に関する情報（共通事項を除く）
 */
export interface IUnmanagedBusinessTypeSalesResult {
  /**
   * 販売実績
   */
  salesResults: IBusiness['salesResults'];
  /**
   * 売り上げ先名
   */
  customerName: IBusiness['customerName'];
  /**
   * 売り上げ先コード
   */
  customerNumber: IBusiness['customerNumber'];
  /**
   * 送り先名
   */
  salesDestinationName: IBusiness['salesDestinationName'];
  /**
   * 送り先コード
   */
  salesDestinationNumber: IBusiness['salesDestinationNumber'];
  /**
   * エンドユーザー
   */
  endUser: IBusiness['endUser'];
}

/**
 * 未管理の取引の在庫に関する情報（共通事項を除く）
 */
export interface IUnmanagedBusinessTypeInventory {
  /**
   * 在庫
   */
  inventory: IBusiness['inventory'];
  /**
   * 在庫リスト
   */
  inventories: IBusiness['mvpInventory'];
  /**
   * 製造社
   */
  manufacturer: IBusiness['manufacturer'];
}

/**
 * 未管理の取引の入荷に関する情報（共通事項を除く）
 */
export interface IUnmanagedBusinessTypeArrival {
  /**
   * 入荷数量
   */
  arrival: IArrival['amount'];
  /**
   * 入荷リスト
   */
  arrivals: IArrival[];
  /**
   * 製造社
   */
  manufacturer: IBusiness['manufacturer'];
}

/**
 * 販売実績・内示・在庫・入荷のエクセルファイルの種類(Enum)
 */
export const ImportFileType = {
  /**
   * 内示
   */
  Announce: 'announce',
  /**
   * 販売実績
   */
  SalesResult: 'salesResult',
  /**
   * 在庫
   */
  Inventory: 'inventory',
  /**
   * 入荷
   */
  Arrival: 'arrival',
} as const;

/**
 * 販売実績・内示・在庫・入荷のエクセルファイルの種類
 */
export type ImportFileType = (typeof ImportFileType)[keyof typeof ImportFileType];

const SortKeyCommon = {
  inputDate: 'inputDate',
  fileName: 'fileName',
  warehouseNumber: 'warehouseNumber',
  warehouse: 'warehouse',
  supplierNumber: 'supplierNumber',
  supplierName: 'supplierName',
  businessNumberNamedByUser: 'businessNumber',
  businessName: 'businessName',
  package: 'package',
  unit: 'unit',
};

export const SortKeyByFileType = {
  [ImportFileType.Announce]: {
    ...SortKeyCommon,
    'customerAnnounce-1': 'announce-1',
    'customerAnnounce-2': 'announce-2',
    'customerAnnounce-3': 'announce-3',
    customerNumber: 'customerNumber',
    customerName: 'customerName',
    deliveryDestinationNumber: 'salesDestinationNumber',
    deliveryDestinationName: 'salesDestinationName',
  },
  [ImportFileType.SalesResult]: {
    ...SortKeyCommon,
    salesQuantity: 'salesResults',
    customerNumber: 'customerNumber',
    customerName: 'customerName',
    deliveryDestinationNumber: 'salesDestinationNumber',
    deliveryDestinationName: 'salesDestinationName',
    enduser: 'endUser',
    stdInventoryIndex: 'stdInventoryIndices',
  },
  [ImportFileType.Inventory]: {
    ...SortKeyCommon,
    inventory: 'inventory',
    manufacturer: 'manufacturer',
  },
  [ImportFileType.Arrival]: {
    ...SortKeyCommon,
    arrivalAmount: 'arrival',
    manufacturer: 'manufacturer',
  },
} as const;

export const SortKeyByFileTypeKeyNameArray = {
  [ImportFileType.Announce]: Object.keys(SortKeyByFileType[ImportFileType.Announce]),
  [ImportFileType.SalesResult]: Object.keys(SortKeyByFileType[ImportFileType.SalesResult]),
  [ImportFileType.Inventory]: Object.keys(SortKeyByFileType[ImportFileType.Inventory]),
  [ImportFileType.Arrival]: Object.keys(SortKeyByFileType[ImportFileType.Arrival]),
} as const;
